/**
 * Export stĺpov z mapy do CSV a KML.
 *
 * Beží celý v prehliadači, rovnako ako import (`csvImporter.ts`) — netreba
 * naň backend a dáta už v store sú.
 *
 * CSV je zámerne spravené tak, aby ho appka vedela znova naimportovať:
 * importér hľadá stĺpec so šírkou podľa vzorov ['latitude','lat','sirka',
 * 'šírka','y'] a prvá zhoda vyhráva. Hlavička ako „typ" obsahuje písmeno y,
 * takže keby bola pred „sirka", importér by za zemepisnú šírku považoval typ
 * stĺpa. Preto idú súradnice ako prvé dva stĺpce.
 */
import { Pole } from '../types';
import { POLE_TYPE_LABELS } from './poleLabels';

export type ExportFormat = 'csv' | 'kml';

export interface ExportOptions {
  /** Vynechať virtuálne (zákaznícke) stĺpy — sedia na tých istých súradniciach ako rodič */
  bezVirtualnych?: boolean;
}

const cislo = (v: number | string | undefined): number | null => {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

/**
 * Pole do CSV: úvodzovky sa zdvojujú, oddeľovač je bodkočiarka (Excel v SK).
 *
 * Nové riadky sa nahrádzajú medzerou zámerne. Importér (`parseCsvText`) delí
 * súbor na riadky ešte pred spracovaním úvodzoviek, takže viacriadkové pole by
 * síce bolo podľa normy správne, ale appka by si taký súbor už nenačítala —
 * a vyrobiť export, ktorý sa nedá naimportovať späť, je horšie než stratiť
 * zalomenie v poznámke.
 */
const csvPole = (hodnota: string | number | null | undefined): string => {
  const s = (hodnota === null || hodnota === undefined ? '' : String(hodnota))
    .replace(/[\r\n]+/g, ' ');
  return /[";]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const vyberStlpov = (poles: Pole[], moznosti: ExportOptions = {}): Pole[] =>
  poles.filter((p) => {
    if (moznosti.bezVirtualnych !== false && p.is_virtual) return false;
    return cislo(p.latitude) !== null && cislo(p.longitude) !== null;
  });

export const doCsv = (poles: Pole[]): string => {
  const hlavicka = ['sirka', 'dlzka', 'nazov', 'typ', 'poznamka'];
  const riadky = poles.map((p) => [
    cislo(p.latitude)!.toFixed(7),
    cislo(p.longitude)!.toFixed(7),
    p.name || '',
    POLE_TYPE_LABELS[p.pole_type] || p.pole_type || '',
    p.notes || '',
  ].map(csvPole).join(';'));
  // BOM, aby Excel otvoril diakritiku správne
  return '﻿' + [hlavicka.join(';'), ...riadky].join('\r\n') + '\r\n';
};

const xml = (s: string): string =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const doKml = (poles: Pole[], nazovProjektu: string): string => {
  const miesta = poles.map((p) => {
    const popis = [
      POLE_TYPE_LABELS[p.pole_type] || p.pole_type,
      p.notes || '',
    ].filter(Boolean).join(' · ');
    // KML má súradnice v poradí dĺžka,šírka[,výška] — opačne než CSV
    return `    <Placemark>
      <name>${xml(p.name || `Stĺp ${p.id}`)}</name>
      <description>${xml(popis)}</description>
      <Point><coordinates>${cislo(p.longitude)},${cislo(p.latitude)},0</coordinates></Point>
    </Placemark>`;
  }).join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${xml(nazovProjektu)} — stĺpy</name>
${miesta}
  </Document>
</kml>
`;
};

/** Bezpečný názov súboru — diakritika a medzery robia problém na Windows aj v maili. */
export const nazovSuboru = (nazovProjektu: string, format: ExportFormat): string => {
  const zaklad = (nazovProjektu || 'projekt')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'projekt';
  const datum = new Date().toISOString().slice(0, 10);
  return `stlpy-${zaklad}-${datum}.${format}`;
};

export const stiahni = (obsah: string, nazov: string, mime: string): void => {
  const blob = new Blob([obsah], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nazov;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Uvoľniť až po kliknutí, inak Safari stiahne prázdny súbor
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportujStlpy = (
  poles: Pole[],
  nazovProjektu: string,
  format: ExportFormat,
  moznosti: ExportOptions = {},
): number => {
  const vybrane = vyberStlpov(poles, moznosti);
  if (vybrane.length === 0) return 0;
  if (format === 'csv') {
    stiahni(doCsv(vybrane), nazovSuboru(nazovProjektu, 'csv'), 'text/csv');
  } else {
    stiahni(doKml(vybrane, nazovProjektu), nazovSuboru(nazovProjektu, 'kml'),
            'application/vnd.google-earth.kml+xml');
  }
  return vybrane.length;
};
